import { useEffect, useRef, useState } from "react";
import { adminScreen, type AssistTurn } from "@greg-chick/shared";
import { askAssist, collectAssistState } from "../lib/contentApi";

/**
 * 🙋 도우미 — "어디서 고치지?"에 답하고 **그 화면을 열어준다**(FR-ADMIN).
 *
 * 도우미는 읽고 안내만 한다. 업로드·게시·삭제를 하지 않는다 —
 * 이 서비스의 안전 설계는 "AI는 초안, 사람이 승인"이고, 도우미만 예외로 두면 원칙이 무너진다.
 * 운영자의 진짜 문제도 "고치기 어려워서"가 아니라 "어디서 고치는지 몰라서"다.
 */
const EXAMPLES = [
  "경주존 첨성대 그림 바꾸고 싶어",
  "홈 화면 제목을 정식 오픈으로 바꾸려면?",
  "지금 안 채운 게 뭐가 있어?",
  "QR 코드가 엉뚱한 데로 가요",
];

interface Bubble extends AssistTurn {
  goto?: string;
  highlight?: string;
}

export function AssistPanel({
  screen,
  onGo,
  onClose,
}: {
  screen: string;
  onGo: (screenId: string, highlight?: string) => void;
  onClose: () => void;
}) {
  const [turns, setTurns] = useState<Bubble[]>([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end" });
  }, [turns, busy]);

  async function ask(text: string) {
    const question = text.trim();
    if (!question || busy) return;
    setQ("");
    setErr(null);
    setTurns((t) => [...t, { role: "user", text: question }]);
    setBusy(true);
    try {
      // 상태는 물어볼 때마다 새로 모은다 — 방금 바꾼 것이 답에 반영되게.
      const state = await collectAssistState(screen);
      const history = turns.map((t) => ({ role: t.role, text: t.text }));
      const r = await askAssist({ question, state, history });
      setTurns((t) => [
        ...t,
        { role: "assistant", text: r.answer.reply, goto: r.answer.goto, highlight: r.answer.highlight },
      ]);
    } catch (e) {
      setErr((e as Error).message);
    }
    setBusy(false);
  }

  return (
    <aside className="assist" aria-label="도우미">
      <header className="assist__top">
        <b>🙋 도우미</b>
        <span className="assist__sub">어디서 고치는지 알려드려요</span>
        <button type="button" className="btn btn--sm" onClick={onClose} aria-label="도우미 닫기">✕</button>
      </header>

      <div className="assist__log">
        {turns.length === 0 ? (
          <div className="assist__intro">
            <p className="hint">
              무엇을 고치고 싶은지 편하게 물어보세요. 제가 <b>화면을 찾아 열어드려요.</b>
              <br />
              (저는 안내만 해요 — 올리고 저장하는 건 직접 하셔야 해요)
            </p>
            <div className="assist__examples">
              {EXAMPLES.map((e) => (
                <button key={e} type="button" className="btn btn--sm" onClick={() => void ask(e)}>
                  {e}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {turns.map((t, i) => (
          <div key={i} className="assist__turn" data-role={t.role}>
            <p>{t.text}</p>
            {t.goto ? (
              <button
                type="button"
                className="btn btn--sm btn--primary"
                onClick={() => onGo(t.goto!, t.highlight)}
              >
                👉 {adminScreen(t.goto)?.label ?? "그 화면"} 열기
              </button>
            ) : null}
          </div>
        ))}

        {busy ? <p className="assist__busy">생각하는 중…</p> : null}
        {err ? <p className="msg" data-kind="err">{err}</p> : null}
        <div ref={endRef} />
      </div>

      <form
        className="assist__ask"
        onSubmit={(e) => {
          e.preventDefault();
          void ask(q);
        }}
      >
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="무엇을 고치고 싶으세요?"
          maxLength={300}
          aria-label="도우미에게 물어보기"
        />
        <button type="submit" className="btn btn--primary" disabled={busy || !q.trim()}>
          물어보기
        </button>
      </form>
    </aside>
  );
}
